import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import './ConfirmDialog.css';

const ConfirmContext = createContext(null);

export function ConfirmProvider({ children }) {
  const [state, setState] = useState(null);
  const resolveRef = useRef(null);

  const confirm = useCallback((message, { title = 'Confirm action', confirmLabel = 'Confirm', cancelLabel = 'Cancel' } = {}) => {
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setState({ title, message, confirmLabel, cancelLabel });
    });
  }, []);

  const close = useCallback((result) => {
    setState(null);
    resolveRef.current?.(result);
    resolveRef.current = null;
  }, []);

  return (
    <ConfirmContext.Provider value={{ confirm }}>
      {children}
      {state && (
        <div className="lms-confirm-overlay" role="presentation" onClick={() => close(false)}>
          <div
            className="lms-confirm-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="confirm-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="confirm-title" className="lms-confirm-dialog__title">{state.title}</h2>
            <p className="lms-confirm-dialog__message">{state.message}</p>
            <div className="lms-confirm-dialog__actions">
              <button type="button" className="lms-btn lms-btn--secondary" onClick={() => close(false)}>
                {state.cancelLabel}
              </button>
              <button type="button" className="lms-btn lms-btn--primary" onClick={() => close(true)}>
                {state.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used within ConfirmProvider');
  return ctx.confirm;
}

/** Global helper for legacy call sites migrating off window.confirm */
let globalConfirm = null;

export function setGlobalConfirm(fn) {
  globalConfirm = fn;
}

export async function confirmAction(message, options) {
  if (globalConfirm) {
    return globalConfirm(message, options);
  }
  return window.confirm(message);
}
